import React from "react";
import { Link } from "react-router-dom";
import { useProfile, useSyncTransaction, timeAgoByMax } from "@moonscape/shared";
import { getExchangeValues } from "@/components/Sidebar/getExchangeValues";
import { ArrowPathIcon, PlusCircleIcon } from "@heroicons/react/24/outline";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyWidget } from "@/components/ui/empty-widget";
import { SyncStatus } from "@/types/sync";
import { BrandMark, Callout, DHead, Fig, Tag } from "@/components/ms";
import { HeadLink } from "./chrome";

export const ConnectedExchangesWidget = () => {
  const { profile, isQueryLoading } = useProfile();
  const linkedExchangesArr = getExchangeValues(profile?.linkedExchangeAccounts);
  const { sync } = useSyncTransaction({
    onSuccess: () => null,
    onError: () => null
  });

  // Sort exchanges: failed first, then others
  const exchanges = (linkedExchangesArr || []).sort((a, b) => {
    const aFailed = a.syncStatus === SyncStatus.FAILED;
    const bFailed = b.syncStatus === SyncStatus.FAILED;
    if (aFailed && !bFailed) return -1;
    if (!aFailed && bFailed) return 1;
    return 0;
  });

  // Count failed exchanges
  const failedCount = exchanges.filter(e => e.syncStatus === SyncStatus.FAILED).length;

  // When there are failed exchanges, show only max 3 working exchanges
  const workingExchanges = exchanges.filter(e => e.syncStatus === SyncStatus.COMPLETED);
  const displayedExchanges = failedCount > 0
    ? workingExchanges.slice(0, 3)
    : exchanges;

  const handleSync = async (e: React.MouseEvent, exchangeId: string) => {
    e.preventDefault();
    e.stopPropagation();
    await sync(exchangeId);
  };

  // Loading state
  if (isQueryLoading) {
    return (
      <section className="flex flex-col">
        <DHead label="Sources" />
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-3 border-t border-ms-line py-[11px] first:border-t-0">
            <Skeleton width={28} height={28} />
            <Skeleton className="h-4 flex-1" />
          </div>
        ))}
      </section>
    );
  }

  // Empty state
  if (exchanges.length === 0) {
    return (
      <section className="flex flex-col">
        <DHead label="Sources" />
        <EmptyWidget
          icon={<PlusCircleIcon className="h-8 w-8" />}
          title="No sources connected"
          description="Link an exchange or wallet to import your transactions"
          action={{ label: "Connect Exchange", to: "/settings/exchange/new" }}
        />
      </section>
    );
  }

  return (
    <section className="flex flex-col">
      <DHead
        label="Sources"
        right={<HeadLink to="/settings/exchange/new">Add</HeadLink>}
      />

      {failedCount > 0 && (
        <Callout
          tone="neg"
          className="mt-3.5"
          go={{ label: "Update keys", to: "/integrations" }}
        >
          <b>
            {failedCount} API key{failedCount !== 1 ? "s" : ""} need
            {failedCount === 1 ? "s" : ""} attention.
          </b>{" "}
          Update the credentials and syncing resumes.
        </Callout>
      )}

      <div className={failedCount > 0 ? "mt-3.5 flex flex-col" : "flex flex-col"}>
        {displayedExchanges.map((exchange) => {
          const isFailed = exchange.syncStatus === SyncStatus.FAILED;
          const name = String(exchange.name || "Unknown");

          return (
            <div
              key={exchange.id}
              className="group relative border-t border-ms-line transition-colors first:border-t-0 hover:bg-ms-card2"
            >
              {/* full-row link, so the sync button can sit above it */}
              <Link to={exchange.href} className="absolute inset-0" aria-label={name} />
              <Fig
                lead={<BrandMark brand={name} />}
                label={name}
                sub={
                  isFailed
                    ? "Keys expired"
                    : exchange.lastSynced
                    ? `Synced ${timeAgoByMax(exchange.lastSynced)} ago`
                    : (exchange as any).is_csv
                    ? "Manual import"
                    : "Never synced"
                }
                tag={
                  isFailed ? (
                    <Tag tone="neg">Fix</Tag>
                  ) : exchange.syncStatus === SyncStatus.COMPLETED ? (
                    <Tag tone="pos">Synced</Tag>
                  ) : (
                    <Tag tone="neu">{String(exchange.syncStatus || "Idle")}</Tag>
                  )
                }
                className="pointer-events-none min-h-[44px] border-t-0 pr-9"
              />
              <button
                type="button"
                onClick={(e) => handleSync(e, exchange?.integration_id || exchange.id)}
                className="absolute right-0 top-1/2 z-10 grid h-8 w-8 -translate-y-1/2 place-items-center text-ms-dim opacity-0 transition-opacity hover:text-ms-link focus-visible:opacity-100 group-hover:opacity-100"
                title="Sync now"
                aria-label={`Sync ${name} now`}
              >
                <ArrowPathIcon className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>

      {failedCount > 0 && (
        <Link
          to="/integrations"
          className="mt-3.5 inline-flex min-h-[40px] items-center font-mono text-[9.5px] uppercase tracking-[0.13em] text-ms-link transition-colors hover:text-ms-ink"
        >
          Manage sources →
        </Link>
      )}
    </section>
  );
};
